/**
 * Domain-aware portal detection.
 * - flexpay.today                           → Partner & Customer portal
 * - dev.flexpay.today, power.flexpay.today  → Admin / Approver / Sub-admin portal
 * - theqrzone.com                           → QR Zone gift card storefront (consumer-only)
 *
 * In local dev (localhost / preview), all portals are accessible.
 */

// Admin hostnames. `power.flexpay.today` was added Feb 2026 as the production
// admin domain (alongside `dev.flexpay.today` which stays live for staging).
// Missing it here made admin logins bounce back to dev.flexpay.today via
// validateRoleForDomain() below — the reported "admin panel not working" bug.
const ADMIN_HOSTNAMES = ["dev.flexpay.today", "power.flexpay.today"];
const QR_ZONE_HOSTNAMES = ["theqrzone.com", "www.theqrzone.com"];

export const isAdminPortal = () => {
  const host = window.location.hostname;
  return ADMIN_HOSTNAMES.includes(host);
};

export const isQrZonePortal = () => {
  const host = window.location.hostname;
  return QR_ZONE_HOSTNAMES.includes(host);
};

export const isPartnerPortal = () => {
  const host = window.location.hostname;
  if (ADMIN_HOSTNAMES.includes(host)) return false;
  if (QR_ZONE_HOSTNAMES.includes(host)) return false;
  if (host === "localhost" || host.includes("preview.emergentagent.com")) return false;
  return true;
};

/** True when running locally or on preview — show everything */
export const isDevEnvironment = () => {
  const host = window.location.hostname;
  return host === "localhost" || host.includes("preview.emergentagent.com");
};

/** Admin-side roles */
export const ADMIN_ROLES = ["admin", "approver_l1", "approver_l2", "approver_l3", "approver_all"];
export const isAdminRole = (role) =>
  ADMIN_ROLES.includes(role) || (typeof role === "string" && role.startsWith("custom_admin_"));

/** Partner-side roles */
export const isPartnerRole = (role) =>
  role === "partner" || role === "user" || (typeof role === "string" && role.startsWith("custom_partner_"));

/**
 * After login, check if the user's role belongs on this domain.
 * Returns { allowed: true } or { allowed: false, redirect: "https://..." }
 */
export const validateRoleForDomain = (role) => {
  if (isDevEnvironment()) return { allowed: true };

  if (isAdminPortal() && !isAdminRole(role)) {
    // Partner tried to login on admin domain
    return { allowed: false, redirect: `https://flexpay.today/login` };
  }
  if (isPartnerPortal() && isAdminRole(role)) {
    // Admin tried to login on partner domain
    return { allowed: false, redirect: `https://dev.flexpay.today/login` };
  }
  return { allowed: true };
};
